import React, { useState, useEffect, useRef } from 'react';
import { Megaphone, Plus, Trash2, Search, Pin, PinOff, Check, ChevronDown, X, Eye, Archive, ArchiveRestore } from 'lucide-react';
import { announcementService } from '../../services';
import { Modal, Button, PageHeader } from '../../components';
import { AnnouncementsSkeleton } from '../../skeletons';
import { toast } from 'react-hot-toast';

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  // Older — show a short date e.g. "15 May"
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const isNew = (iso) => {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 24 * 60 * 60 * 1000;
};

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

// Audience groups are returned by the backend with already-friendly names
// (Carers / Management). This helper just gives a safe fallback for any
// unmapped code so the UI never renders a blank chip.
const roleDisplayName = (code, fallback) => fallback || code || 'Staff';

/**
 * SpecificPeoplePicker — click-to-open multi-select used by the "Specific
 * people" tab of the New Announcement modal. Selected staff appear as
 * dismissible chips inside the trigger; clicking the trigger reveals a
 * scrollable checklist of every staff member. Clicking outside closes it.
 */
const SpecificPeoplePicker = ({ staff, selectedIds, onToggle, onRemove, onSelectAll }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = new Set(selectedIds);
  const selectedStaff = staff.filter((s) => selectedSet.has(s.id));
  const allIds = staff.map((s) => s.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));

  return (
    <div className="mt-3" ref={wrapRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`w-full flex items-center justify-between gap-2 min-h-[44px] px-3 py-2 rounded-xl border bg-white dark:bg-[#31373f] text-left transition ${
            open ? 'border-blue-400 ring-2 ring-blue-500/10' : 'border-[#E2E8F0] dark:border-gray-700 hover:border-blue-200'
          }`}
        >
          <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
            {selectedStaff.length === 0 ? (
              <span className="text-[13px] text-[#94A3B8] dark:text-gray-500 font-medium pl-1">Click to add staff…</span>
            ) : (
              selectedStaff.map((u) => (
                <span
                  key={u.id}
                  className="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400 text-[11px] font-bold border border-blue-100 dark:border-blue-900/50"
                >
                  <span className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center text-[8px] font-black">
                    {initials(u.name)}
                  </span>
                  {u.name}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); onRemove(u.id); }}
                    className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-100"
                    title="Remove"
                  >
                    <X className="w-3 h-3" strokeWidth={3} />
                  </span>
                </span>
              ))
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-[#94A3B8] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute top-[calc(100%+6px)] left-0 right-0 z-10 bg-white dark:bg-[#262b33] rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-[#E2E8F0] dark:border-gray-800 max-h-[220px] overflow-y-auto p-1">
            {staff.length === 0 ? (
              <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 italic p-3">No staff available.</p>
            ) : (
              <>
                {/* Select All — toggles every staff member at once. Lives at
                    the top of the list so it's always discoverable. */}
                {onSelectAll && (
                  <button
                    type="button"
                    onClick={() => onSelectAll(allIds)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg border-b border-[#F1F5F9] dark:border-gray-800 transition ${allSelected ? 'bg-blue-50 dark:bg-blue-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${allSelected ? 'border-[#2563EB] bg-[#2563EB]' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                      {allSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <span className="text-[12px] font-black uppercase tracking-wide text-[#2563EB] text-left flex-1">
                      Select all ({staff.length})
                    </span>
                  </button>
                )}
                {staff.map((u) => {
                const active = selectedSet.has(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => onToggle(u.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition ${
                      active ? 'bg-blue-50 dark:bg-blue-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                      active ? 'border-[#2563EB] bg-[#2563EB]' : 'border-[#E2E8F0] dark:border-gray-600'
                    }`}>
                      {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-[#2563EB] dark:text-blue-300 text-[9px] font-black flex items-center justify-center shrink-0">
                      {initials(u.name)}
                    </div>
                    <div className="min-w-0 text-left flex-1">
                      <p className="text-[12.5px] font-semibold text-[#1E293B] dark:text-gray-200 truncate">{u.name}</p>
                      {u.role_group_name && (
                        <p className="text-[10px] text-[#94A3B8] dark:text-gray-500 uppercase tracking-tight font-bold">{u.role_group_name}</p>
                      )}
                    </div>
                  </button>
                );
              })}
              </>
            )}
          </div>
        )}
      </div>

      <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-2 leading-snug">
        Only the people you select will see this. Click <X className="inline w-3 h-3 align-middle" /> on any chip to deselect just that person.
      </p>
    </div>
  );
};

/**
 * LinkedResidentsPicker — multi-select for the "Linked clients" field of
 * the New Announcement modal. Mirrors SpecificPeoplePicker but styled in
 * emerald to match the broader convention that staff = blue, clients =
 * green throughout the manager UI. Supports Select All.
 */
const LinkedResidentsPicker = ({ residents, selectedIds, onToggle, onRemove, onSelectAll }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = new Set(selectedIds);
  const selectedResidents = residents.filter((r) => selectedSet.has(r.id));
  const allIds = residents.map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));

  return (
    <div ref={wrapRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`w-full flex items-center justify-between gap-2 min-h-[44px] px-3 py-2 rounded-xl border bg-white dark:bg-[#31373f] text-left transition ${
            open ? 'border-emerald-400 ring-2 ring-emerald-500/10' : 'border-[#E2E8F0] dark:border-gray-700 hover:border-emerald-200'
          }`}
        >
          <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
            {selectedResidents.length === 0 ? (
              <span className="text-[13px] text-[#94A3B8] dark:text-gray-500 font-medium pl-1">Click to add clients…</span>
            ) : (
              selectedResidents.map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold border border-emerald-100 dark:border-emerald-900/50"
                >
                  <span className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center text-[8px] font-black">
                    {initials(r.name)}
                  </span>
                  {r.name}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); onRemove(r.id); }}
                    className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-emerald-100"
                    title="Remove"
                  >
                    <X className="w-3 h-3" strokeWidth={3} />
                  </span>
                </span>
              ))
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-[#94A3B8] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute top-[calc(100%+6px)] left-0 right-0 z-10 bg-white dark:bg-[#262b33] rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.12)] border border-[#E2E8F0] dark:border-gray-800 max-h-[220px] overflow-y-auto p-1">
            {residents.length === 0 ? (
              <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 italic p-3">No clients available.</p>
            ) : (
              <>
                {onSelectAll && (
                  <button
                    type="button"
                    onClick={() => onSelectAll(allIds)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg border-b border-[#F1F5F9] dark:border-gray-800 transition ${allSelected ? 'bg-emerald-50 dark:bg-emerald-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${allSelected ? 'border-emerald-600 bg-emerald-600' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                      {allSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </div>
                    <span className="text-[12px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-400 text-left flex-1">
                      Select all ({residents.length})
                    </span>
                  </button>
                )}
                {residents.map((r) => {
                  const active = selectedSet.has(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => onToggle(r.id)}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg transition ${active ? 'bg-emerald-50 dark:bg-emerald-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${active ? 'border-emerald-600 bg-emerald-600' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                        {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </div>
                      <div className="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[9px] font-black flex items-center justify-center shrink-0">
                        {initials(r.name)}
                      </div>
                      <span className="text-[12.5px] font-semibold text-[#1E293B] dark:text-gray-200 text-left flex-1 truncate">{r.name}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Announcements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'pinned' | 'archived'
  // Tracked separately from `items` because archived rows are excluded from
  // the other tabs' responses, so the count can't be derived from `items`.
  const [archivedCount, setArchivedCount] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Id of the announcement whose read-receipt list is expanded.
  const [seenOpen, setSeenOpen] = useState(null);

  // Form
  const [form, setForm] = useState({
    title: '',
    body: '',
    audience_type: 'all',
    audience_roles: [],
    audience_user_ids: [],
    linked_resident_ids: [],
    is_pinned: false,
  });
  const [options, setOptions] = useState({ roles: [], staff: [], residents: [] });
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  // Locally-tracked "read" set. Real backend tracking is out of scope here,
  // so we treat "unread" as anything created in the last 24h that the
  // current user hasn't dismissed by clicking. Persists in localStorage.
  const [readIds, setReadIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem('announcements_read');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (e) { return new Set(); }
  });

  const persistRead = (next) => {
    setReadIds(next);
    try { window.localStorage.setItem('announcements_read', JSON.stringify([...next])); } catch (e) {}
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await announcementService.list({
        // 'pinned' and 'archived' are resolved server-side; 'unread' is a
        // client-side view over the active (non-archived) list.
        filter: (filter === 'pinned' || filter === 'archived') ? filter : undefined,
        search: search.trim() || undefined,
      });
      if (res.success) setItems(res.data || []);

      // Keep the Archived tab counter accurate. On the Archived tab the main
      // response already IS the archived set; on any other tab those rows are
      // excluded, so fetch them once to get the count.
      if (filter === 'archived') {
        if (res.success) setArchivedCount((res.data || []).length);
      } else {
        const arch = await announcementService.list({
          filter: 'archived',
          search: search.trim() || undefined,
        });
        if (arch.success) setArchivedCount((arch.data || []).length);
      }
    } catch (err) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    if (optionsLoaded) return;
    try {
      const res = await announcementService.options();
      if (res.success) setOptions(res.data);
      setOptionsLoaded(true);
    } catch (err) {
      toast.error('Failed to load audience options');
    }
  };

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search]);

  const handleOpenCreate = () => {
    setForm({
      title: '',
      body: '',
      audience_type: 'all',
      audience_roles: [],
      audience_user_ids: [],
      linked_resident_ids: [],
      is_pinned: false,
    });
    setCreateOpen(true);
    loadOptions();
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.body.trim()) { toast.error('Message is required'); return; }
    if (form.audience_type === 'by_role' && form.audience_roles.length === 0) {
      toast.error('Pick at least one role'); return;
    }
    if (form.audience_type === 'specific' && form.audience_user_ids.length === 0) {
      toast.error('Pick at least one staff member'); return;
    }
    try {
      setSaving(true);
      const res = await announcementService.create({
        title: form.title.trim(),
        body: form.body.trim(),
        audience_type: form.audience_type,
        audience_roles: form.audience_type === 'by_role' ? form.audience_roles : [],
        audience_user_ids: form.audience_type === 'specific' ? form.audience_user_ids : [],
        linked_resident_ids: form.linked_resident_ids,
        is_pinned: form.is_pinned,
      });
      if (res.success) {
        toast.success('Announcement posted');
        setCreateOpen(false);
        fetchAll();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await announcementService.remove(deleteConfirm.id);
      toast.success('Removed');
      setDeleteConfirm(null);
      fetchAll();
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePin = async (a) => {
    try {
      await announcementService.togglePin(a.id);
      toast.success(a.is_pinned ? 'Unpinned' : 'Pinned to top');
      fetchAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleToggleArchive = async (a) => {
    try {
      await announcementService.toggleArchive(a.id);
      toast.success(a.is_archived ? 'Restored' : 'Archived');
      fetchAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const markRead = (id) => {
    if (readIds.has(id)) return;
    const next = new Set(readIds);
    next.add(id);
    persistRead(next);
  };

  // "Unread" = posted in the last 24h AND not locally marked as read.
  const isUnread = (a) => isNew(a.created_at) && !readIds.has(a.id);

  // Apply the unread filter client-side since the backend doesn't track
  // per-user read state (yet). Search + pinned filtering happen server-side.
  const visibleItems = items.filter((a) => {
    if (filter === 'unread' && !isUnread(a)) return false;
    return true;
  });

  const unreadCount = items.filter(isUnread).length;
  const pinnedCount = items.filter((a) => a.is_pinned).length;

  // Toggle helpers for the audience pickers
  const toggleRole = (code) => {
    setForm((f) => {
      const exists = f.audience_roles.includes(code);
      return { ...f, audience_roles: exists ? f.audience_roles.filter((c) => c !== code) : [...f.audience_roles, code] };
    });
  };

  const toggleUser = (id) => {
    setForm((f) => {
      const exists = f.audience_user_ids.includes(id);
      return { ...f, audience_user_ids: exists ? f.audience_user_ids.filter((x) => x !== id) : [...f.audience_user_ids, id] };
    });
  };

  const toggleResident = (id) => {
    setForm((f) => {
      const exists = f.linked_resident_ids.includes(id);
      return { ...f, linked_resident_ids: exists ? f.linked_resident_ids.filter((x) => x !== id) : [...f.linked_resident_ids, id] };
    });
  };

  // Toggle the "select all" state of a multi-select field — if every
  // available id is already selected, clears the array; otherwise selects
  // every id at once.
  const toggleSelectAll = (field, allIds) => {
    setForm((f) => {
      const current = f[field] || [];
      const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
      return { ...f, [field]: allSelected ? [] : [...allIds] };
    });
  };

  // Render the "To: …" chip row for non-everyone audiences
  const renderAudienceLine = (a) => {
    if (a.audience_type === 'all') return null;
    if (a.audience_type === 'by_role') {
      return (
        <div className="flex items-center gap-2 mt-1 mb-2">
          <span className="text-[12px] font-bold text-[#1E293B] dark:text-gray-200">To:</span>
          {(a.audience_role_labels || []).map((r) => (
            <span key={r.code} className="text-[12px] font-semibold text-[#475569] dark:text-gray-400">
              {roleDisplayName(r.code, r.name)}
            </span>
          ))}
        </div>
      );
    }
    // Specific people
    return (
      <div className="flex items-center gap-2 mt-1 mb-2 flex-wrap">
        <span className="text-[12px] font-bold text-[#1E293B] dark:text-gray-200">To:</span>
        {(a.audience_users || []).map((u) => (
          <span
            key={u.id}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400 text-[11px] font-bold"
          >
            <span className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center text-[8px] font-black">
              {initials(u.name)}
            </span>
            {u.name}
          </span>
        ))}
      </div>
    );
  };

  if (loading && items.length === 0) return <AnnouncementsSkeleton />;

  return (
    <div className="w-full pb-10 font-sans">
      {/* Header */}
      <PageHeader
        title="Announcements"
        count={items.length || undefined}
        subtitle="Policy updates, team notices, and important comms for staff"
        actions={(
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={handleOpenCreate}>
            New Announcement
          </Button>
        )}
      />

      {/* Search + filter pills */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mt-6 mb-6">
        <div className="relative md:w-[320px]">
          <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search announcements…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 w-full pl-9 pr-3 rounded-xl bg-[#F6F9FC] dark:bg-[#31373f] border border-[#EEF2F8] dark:border-gray-700 text-[13.5px] text-[#334155] dark:text-gray-200 placeholder-[#94A3B8] outline-none focus:bg-white dark:focus:bg-[#383e47] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 transition"
          />
        </div>
        <div className="flex items-center gap-1.5 p-1 rounded-full bg-[#EEF2F6] dark:bg-[#1b1f26] border border-[#E3E9F3] dark:border-gray-800 w-fit">
          {[
            { key: 'all', label: 'All', count: null },
            { key: 'unread', label: 'Unread', count: unreadCount },
            { key: 'pinned', label: 'Pinned', count: pinnedCount },
            { key: 'archived', label: 'Archived', count: archivedCount },
          ].map((tab) => {
            const active = filter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition flex items-center gap-1.5 ${
                  active
                    ? 'bg-white dark:bg-[#31373f] text-[#2563EB] dark:text-blue-400 shadow-[0_1px_3px_rgba(15,23,42,0.08)]'
                    : 'text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
                {tab.count !== null && tab.count > 0 && (
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full tabular-nums ${active ? 'bg-blue-50 dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400' : 'bg-white dark:bg-[#31373f] text-[#64748B] dark:text-gray-400'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      {visibleItems.length === 0 ? (
        <div className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-14 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#EEF3FF] dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-4">
            <Megaphone className="w-7 h-7 text-[#2563EB]" />
          </div>
          <p className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200">
            {search.trim()
              ? 'No announcements match your search.'
              : filter === 'pinned'
                ? 'No pinned announcements.'
                : filter === 'unread'
                  ? 'You are all caught up.'
                  : filter === 'archived'
                    ? 'No archived announcements.'
                    : 'No announcements posted yet.'}
          </p>
          <p className="text-[13px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
            {filter === 'all' && !search.trim()
              ? 'Post your first announcement to keep the team in the loop.'
              : 'Try a different filter or search term.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visibleItems.map((a) => {
            const unread = isUnread(a);
            const pinned = a.is_pinned;
            return (
              <div
                key={a.id}
                onClick={() => markRead(a.id)}
                className={`group relative rounded-[20px] border-l-[3px] pl-5 pr-6 py-[18px] cursor-default transition-all shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] hover:shadow-[0_2px_4px_rgba(15,23,42,0.06),0_10px_24px_-6px_rgba(15,23,42,0.12)] ${
                  pinned
                    ? 'bg-[#FFFBEB] dark:bg-amber-900/10 border-l-amber-400 border border-amber-100 dark:border-amber-900/50'
                    : 'bg-white dark:bg-[#262b33] border-l-[#2563EB] border border-[#E3E9F3] dark:border-gray-800'
                }`}
              >
                {/* Top-right pills */}
                <div className="absolute right-5 top-4 flex items-center gap-2">
                  {pinned && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-tight px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400">
                      <Pin className="w-3 h-3" strokeWidth={3} />
                      Pinned
                    </span>
                  )}
                  {unread && (
                    <span className="text-[9px] font-black uppercase tracking-tight px-2 py-1 rounded bg-[#2563EB] text-white">
                      New
                    </span>
                  )}
                </div>

                <div className="flex items-start gap-3 pr-24">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    pinned ? 'bg-amber-100 dark:bg-amber-900/10 text-amber-600 dark:text-amber-400' : 'bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400'
                  }`}>
                    <Megaphone className="w-[18px] h-[18px]" strokeWidth={2.2} />
                  </div>
                  <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200 leading-snug pt-1 min-w-0 break-words">{a.title}</h3>
                </div>

                {renderAudienceLine(a)}

                {a.body && (
                  <p className="text-[12.5px] text-[#475569] dark:text-gray-400 leading-relaxed mt-1 whitespace-pre-line">
                    {a.body}
                  </p>
                )}

                {/* Read receipts — who has seen this announcement */}
                {(() => {
                  const seenBy = a.seen_by || [];
                  const total = a.recipient_count ?? 0;
                  const count = a.seen_count ?? seenBy.length;
                  const expanded = seenOpen === a.id;
                  return (
                    <div className="mt-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSeenOpen(expanded ? null : a.id); }}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-[#64748B] dark:text-gray-400 hover:text-[#2563EB] transition"
                        title="See who has read this"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Seen by {count}{total ? ` / ${total}` : ''}
                        {seenBy.length > 0 && (
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        )}
                      </button>
                      {expanded && (
                        seenBy.length === 0 ? (
                          <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 italic mt-1.5">No one has seen this yet.</p>
                        ) : (
                          <div className="mt-2 flex flex-col gap-1.5 bg-[#F8FAFC] dark:bg-[#20242c] rounded-xl p-2.5 border border-[#F1F5F9] dark:border-gray-800">
                            {seenBy.map((u) => (
                              <div key={u.id} className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-black flex items-center justify-center shrink-0">
                                  {initials(u.name)}
                                </div>
                                <span className="text-[12px] font-semibold text-[#1E293B] dark:text-gray-200 flex-1 truncate">{u.name}</span>
                                <span className="text-[10.5px] text-[#94A3B8] dark:text-gray-500">{formatRelativeTime(u.seen_at)}</span>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  );
                })()}

                {/* Footer: author + actions */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EEF2F8] dark:border-gray-800">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-600 text-[10px] font-black flex items-center justify-center shrink-0">
                      {initials(a.creator?.name)}
                    </div>
                    <p className="text-[12px] font-bold text-[#1E293B] dark:text-gray-200 truncate">
                      {a.creator?.name || 'Unknown'}
                      <span className="text-[#94A3B8] dark:text-gray-500 font-medium"> · {formatRelativeTime(a.created_at)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleTogglePin(a); }}
                      title={pinned ? 'Unpin' : 'Pin to top'}
                      className="p-1.5 rounded-lg text-[#94A3B8] dark:text-gray-500 hover:text-amber-600 hover:bg-amber-50 transition"
                    >
                      {pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleArchive(a); }}
                      title={a.is_archived ? 'Restore' : 'Archive'}
                      className="p-1.5 rounded-lg text-[#94A3B8] dark:text-gray-500 hover:text-[#2563EB] hover:bg-blue-50 transition"
                    >
                      {a.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(a); }}
                      title="Delete"
                      className="p-1.5 rounded-lg text-[#94A3B8] dark:text-gray-500 hover:text-rose-500 hover:bg-rose-50 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Announcement"
        maxWidth="max-w-[560px]"
      >
        <div className="space-y-5">
          {/* Title */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">
              Title <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. New PRN protocol effective 1 June"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">
              Message <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write the announcement body. Plain text. Mention what staff need to do."
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[12.5px] resize-none leading-relaxed"
            />
          </div>

          {/* Audience tabs */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-2">Audience</label>
            <div className="grid grid-cols-3 gap-0 rounded-xl bg-[#F1F5F9] dark:bg-[#31373f] p-1">
              {[
                { key: 'all', label: 'Everyone' },
                { key: 'by_role', label: 'By role' },
                { key: 'specific', label: 'Specific people' },
              ].map((tab) => {
                const active = form.audience_type === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setForm({ ...form, audience_type: tab.key })}
                    className={`py-2 rounded-lg text-[12px] font-bold transition ${
                      active ? 'bg-white dark:bg-[#262b33] text-[#2563EB] dark:text-blue-400 shadow-sm' : 'text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Audience-specific body */}
            {form.audience_type === 'all' && (
              <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-2">
                All current and future staff will see this announcement.
              </p>
            )}

            {form.audience_type === 'by_role' && (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  {options.roles.length === 0 ? (
                    <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 italic">No roles available.</p>
                  ) : (
                    options.roles.map((r) => {
                      const active = form.audience_roles.includes(r.code);
                      return (
                        <button
                          key={r.code}
                          type="button"
                          onClick={() => toggleRole(r.code)}
                          className={`inline-flex items-center px-5 py-1.5 rounded-full text-[13px] font-bold border-2 transition ${
                            active
                              ? 'bg-white dark:bg-[#31373f] border-[#2563EB] text-[#2563EB] dark:text-blue-400'
                              : 'bg-white dark:bg-[#31373f] border-[#E2E8F0] dark:border-gray-700 text-[#64748B] dark:text-gray-400 hover:border-blue-200'
                          }`}
                        >
                          {roleDisplayName(r.code, r.name)}
                        </button>
                      );
                    })
                  )}
                </div>
                <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-2 leading-snug">
                  Staff matching these roles will see the announcement. Newly added staff matching these roles will see it too.
                </p>
              </>
            )}

            {form.audience_type === 'specific' && (
              <SpecificPeoplePicker
                staff={options.staff}
                selectedIds={form.audience_user_ids}
                onToggle={toggleUser}
                onRemove={(id) => toggleUser(id)}
                onSelectAll={(allIds) => toggleSelectAll('audience_user_ids', allIds)}
              />
            )}
          </div>

          {/* Linked clients (optional, multi-select with Select All) */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">
              Linked clients (optional)
            </label>
            <LinkedResidentsPicker
              residents={options.residents}
              selectedIds={form.linked_resident_ids}
              onToggle={toggleResident}
              onRemove={(id) => toggleResident(id)}
              onSelectAll={(allIds) => toggleSelectAll('linked_resident_ids', allIds)}
            />
            <p className="text-[11px] text-[#64748B] dark:text-gray-400 mt-1.5">
              Link this announcement to one or more clients — it will appear on each linked profile
            </p>
          </div>

          {/* Pin to top */}
          <button
            type="button"
            onClick={() => setForm({ ...form, is_pinned: !form.is_pinned })}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition ${
              form.is_pinned ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/50' : 'bg-[#F8FAFC] dark:bg-[#31373f] border-[#E2E8F0] dark:border-gray-700 hover:border-blue-100'
            }`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
              form.is_pinned ? 'border-[#2563EB] bg-[#2563EB]' : 'border-[#CBD5E1] bg-white dark:bg-[#31373f]'
            }`}>
              {form.is_pinned && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </div>
            <div className="text-left flex-1">
              <p className="text-[13px] font-bold text-[#1E293B] dark:text-gray-200">Pin to top</p>
              <p className="text-[11px] text-[#64748B] dark:text-gray-400">Pinned announcements stay at the top until unpinned</p>
            </div>
          </button>

          {/* Footer buttons */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#F1F5F9] dark:border-gray-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleCreate}
              loading={saving}
              disabled={saving}
            >
              {saving ? 'Posting…' : 'Post announcement'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Remove Announcement"
        maxWidth="max-w-[420px]"
      >
        <div className="space-y-5">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mb-3 border border-red-100 dark:border-red-900/50">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-[16px] font-bold text-[#1E293B] dark:text-gray-200">Are you sure?</h3>
            <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-2">
              "{deleteConfirm?.title}" will be removed permanently and no longer appear on the dashboard.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleting}
              disabled={deleting}
              className="flex-1"
            >
              {deleting ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Announcements;
